import { useMemo } from 'react'

import { buildFetchOptionsWithAuth, fetchJson } from '@fv/client-core'
import type { AccountSettings as _AccountSettings, Workflow } from '@fv/models'
import type { DTO } from '@fv/models/core'

import { apiUri } from '@/constants'
import { useUserSettings } from '@/hooks/settings'
import { useCurrentUser } from '@/hooks/settings/useAppContext'
import { useStaticDataQuery } from '@/hooks/useStaticDataQuery'

import { quotingSettings } from './account-settings-config'
import type { AccountSettingsFormItem, DistanceProviderConfig } from './types'

export type AccountSettings = DTO<_AccountSettings>

export const useAccountSettings = (): AccountSettings => {
  const userSettings = useUserSettings()
  if (!userSettings.data.settings) {
    throw new Error('Unable to load settings')
  }
  return userSettings.data?.settings
}

const fetchDistanceProviders = async (): Promise<DistanceProviderConfig[]> => {
  const endpoint = `${apiUri}/accounts/settings/distance-providers`
  const response = await fetchJson(
    endpoint,
    buildFetchOptionsWithAuth({ method: 'GET' }),
  )

  if (response.ok) return response.json
  else throw response.errorMessage
}

export const useAvailableDistanceProviders = () => {
  return useStaticDataQuery(['distance-providers'], fetchDistanceProviders)
}

export const useHasMexico = () => {
  const {
    truckload: { enableMexico },
  } = useAccountSettings()
  return enableMexico
}

export const useShowBidExpiration = () => {
  const {
    quoting: { showBidExpiration },
  } = useAccountSettings()
  return showBidExpiration
}

const ltlDimItem: AccountSettingsFormItem = {
  label: 'Require handling unit dimensions for LTL shipments',
  setting: 'quoting.ltl.requireDims',
}
const ltlNmfcItem: AccountSettingsFormItem = {
  label: 'Require an NMFC item number for LTL shipments',
  setting: 'quoting.ltl.requireNmfc',
}
const parcelDimItem: AccountSettingsFormItem = {
  label: 'Require handling unit dimensions for parcel shipments',
  setting: 'quoting.parcel.requireDims',
}
export const useQuotingSettings = (): AccountSettingsFormItem[] => {
  const { allowedWorkflows } = useCurrentUser()
  return useMemo(
    () => [
      ...quotingSettings,
      ...(allowedWorkflows.includes('ltl') ? [ltlDimItem, ltlNmfcItem] : []),
      ...(allowedWorkflows.includes('parcel') ? [parcelDimItem] : []),
    ],
    [allowedWorkflows],
  )
}

export const useAreDimsRequired = (workflow: Workflow) => {
  const { quoting } = useAccountSettings()
  if (workflow === 'truckload') return false
  return !!quoting[workflow]?.requireDims
}

export const useIsNmfcRequired = (workflow: Workflow) => {
  const { quoting } = useAccountSettings()
  return workflow === 'ltl' && !!quoting.ltl?.requireNmfc
}
